import { centsToUSD } from "app/client/models/BillingModel";
import { urlState } from "app/client/models/gristUrlState";
import { bigBasicButton, bigPrimaryButtonLink } from "app/client/ui2018/buttons";
import { mediaSmall, theme, vars } from "app/client/ui2018/cssVars";
import { icon } from "app/client/ui2018/icons";
import { cssLink } from "app/client/ui2018/links";
import { loadingDots, watchPromise } from "app/client/ui2018/loaders";
import { cssModalTitle } from "app/client/ui2018/modals";
import { IBillingPlan, PlanSelection } from "app/common/BillingAPI";
import { TEAM_FREE_PLAN } from "app/common/Features";
import { inlineStyle } from "app/common/gutil";

import { Computed, Disposable, dom, DomArg, DomContents, makeTestId, Observable, styled } from "grainjs";

const testId = makeTestId("test-productupgrade-");

/**
 * Options to create a single plan card. Each of the option can be either a string or a valid
 * dom element.
 */
export interface PlanContent {
  name: DomContents; // product title (e.g. "Team")
  price?: number; // price per interval per user in cents.
  comment?: string | null; // comment (e.g. "Always free")
  button?: DomContents | (() => DomContents); // button text (e.g. "Upgrade plan")
  gristProduct: string; // product name (e.g. "team" / "teamFree")
  priceId?: string; // price stripe id (e.g. "price_11111") for this product, if empty then default.
  features: DomContents[]; // features (e.g. "Unlimited users")
  interval: string; // interval (e.g. "month")
  free: boolean; // is this plan free
  trialPeriodDays?: number; // trial period in days
  savings?: number; // savings in cents
}

export type ChangeReason = "create" | "upgrade" | "renew";

export class PlanContentModel extends Disposable {
  public name: DomContents;
  public price: number;
  public gristProduct: string;
  public priceId: string | undefined;
  public features: DomContents[];
  public interval: string;
  public free: boolean;
  public trialPeriodDays?: number | undefined;
  public savings: Computed<number | null>;
  public pricePerMonth: Computed<number>;
  public priceToShow: Computed<number>;
  public data: IBillingPlan;
  public allPlans: IBillingPlan[];

  public properCount: Computed<number>;

  private _upSaleFrom: Computed<IBillingPlan | null>;
  private _action = "Upgrade plan";

  constructor(private _options: {
    data: IBillingPlan,
    allPlans: IBillingPlan[],
    reason?: ChangeReason, // default create.
  }) {
    super();

    this.data = _options.data;
    this.allPlans = _options.allPlans;

    this.name = this.data.nickname;
    this.price = this.data.amount;
    this.gristProduct = this.data.product;
    this.priceId = this.data.id;
    this.features = this.data.features.length ?
      this.data.features :
      FEATURES[this.data.product as keyof typeof FEATURES] ?? [];
    this.interval = this.data.interval;
    this.free = this.data.amount === 0;
    this.trialPeriodDays = this.data.trialPeriodDays ?? undefined;

    this.properCount = Computed.create(this, use => 1);

    this._upSaleFrom = Computed.create(this, (use) => {
      // Find plan for the same product but with interval 'month';
      const sameProduct = this.allPlans.find(p => p.product === this.data.product && p.interval === "month");
      if (sameProduct && sameProduct !== this.data) {
        return sameProduct;
      }
      return null;
    });

    this.savings = Computed.create(this, (use) => {
      // If this is custom plan, then we don't have any savings.
      if (!this.data.metadata.isStandard) { return null; }
      const upSaleFrom = use(this._upSaleFrom);
      if (!upSaleFrom) { return null; }
      const savingPerMonth = upSaleFrom.amount * 12 - this.data.amount;
      if (savingPerMonth <= 0) { return null; }
      return use(this.properCount) * savingPerMonth;
    });

    this.pricePerMonth = Computed.create(this, (use) => {
      if (this.interval === "month") {
        return this.price;
      } else {
        return Math.floor(this.price / 12);
      }
    });

    this.priceToShow = Computed.create(this, (use) => {
      return use(this.properCount) * use(this.pricePerMonth);
    });
  }

  public setUpgradeAction(currentPlan: PlanContentModel | undefined) {
    if (this.price === 0) {
      this._action = "Downgrade plan";
      return;
    }

    // Compare plans, if it is worse than current plan, then it is an downgrade.
    if (!currentPlan) { return; }

    // If interval is lower, then it is an downgrade.
    if (this.interval === "year" && currentPlan.interval === "month") {
      this._action = `Pay yearly`;
      return;
    }

    if (this.interval == "month" && currentPlan.interval === "year") {
      this._action = `Downgrade plan`;
      return;
    }

    // If price is lower than current plan, then it is an downgrade.
    if (this.price < currentPlan.price) {
      this._action = "Downgrade plan";
      return;
    }

    this._action = "Upgrade plan";
  }

  public buttonText() {
    if (this._options.reason === "upgrade") {
      return `${this._action}`;
    } else if (this._options.reason === "renew") {
      return "Renew plan";
    } else {
      return this.data.trialPeriodDays ? `Start free ${this.data.trialPeriodDays}-day trial` : "Create site";
    }
  }

  public get comment() {
    const bPlan: IBillingPlan = this.data;
    if ((bPlan.metadata.minimumUnits ?? 1) > 1) {
      return `${bPlan.metadata.minimumUnits} users minimum`;
    }
    return null;
  }
}

export function matchPlan(
  selectedPlan: PlanSelection, p: Pick<PlanContent, "gristProduct" | "priceId" | "interval">,
) {
  if (selectedPlan.product === TEAM_FREE_PLAN) {
    return p.gristProduct === TEAM_FREE_PLAN;
  } else if (selectedPlan.priceId) {
    return p.priceId === selectedPlan.priceId;
  } else {
    return p.gristProduct === selectedPlan.product && p.interval === "month";
  }
}

/**
 * Default feature lists for various plans if Stripe doesn't provide them.
 */
export const FEATURES = {
  team: [
    "No limit on team size",
    "Unlimited documents",
    "100,000 rows per document",
    "3 years snapshot history",
    "API limit: 40k calls/day",
    "Workspaces",
    "Custom subdomain",
    "100 AI Assistant credits per month",
    "Email support",
    "Sprouts program https://www.getgrist.com/sprouts-program/",
  ],
  [TEAM_FREE_PLAN]: [
    "Up to 10 team members",
    "Unlimited documents",
    "5,000 rows per document",
    "30 days snapshot history",
    "API limit: 3k calls/month/team",
    "Workspaces",
    "Custom subdomain",
    "100 AI Assistant credits",
  ],
};

export function buildPlanCard(options: {
  plan: PlanContentModel,
  interval?: Observable<string>,
  isCurrent?: boolean,
  collapsed?: null | Observable<boolean>,
  onClick?: null | (() => unknown),
  onSelect?: (id: string) => unknown,
  upgrade?: boolean, // is this an upgrade modal,
  single?: boolean, // is this a single card (and don't need to render placeholders to align the height)
}, ...args: DomArg<HTMLElement>[]
) {
  if (!options.plan.gristProduct) {
    throw new Error("productName is required");
  }

  let button: HTMLElement;
  const { plan, collapsed, onClick, onSelect } = options;
  const isCurrent = options.isCurrent || false;

  const action = watchPromise(async () => onSelect?.(plan.gristProduct));

  // Create an url for upgrade

  const url = urlState().makeUrl({
    ...(options.upgrade ? { upgradeTeam: true } : { createTeam: true }),
    params: {
      billingPlan: plan.priceId,
      planType: plan.gristProduct,
    },
  });

  return cssPlan(
    ...args,
    cssPlan.cls("-single", !!options.single),
    dom.show(use => !options.interval || use(options.interval) === plan.interval || plan.free),
    testId(`plan-card-${plan.gristProduct}-${plan.interval}`),
    testId(`plan-card-${plan.gristProduct}`),
    testId("plan-card"),
    // for tests
    dom("hidden", { value: plan.gristProduct }, testId("plan-id")),
    dom("hidden", { value: plan.priceId }, testId("price-id")),
    // Click events is for collapsing the plan - collapsed state is manage by the caller.
    // Don't react to clicks on the button.
    dom.on("click", ev => ev.target !== button ? onClick?.() : void 0),
    !collapsed ? null : cssPlan.cls("-collapsed", collapsed),
    cssPlan.cls("-current", isCurrent),
    // Part that will be always visible
    cssAlwaysVisible(
      // Plan name with a price or info that this is the current plan. Info is visible
      // only when collapsed.
      cssPlanName(
        dom("span", plan.name, testId("plan-name")),

        // This is div visible only when collapsed
        cssWhenCollapsed(
          dom.maybe(collapsed, () => [
            dom.maybe(isCurrent, () => cssCurrentPlan("Current plan")),
            dom.maybe(!isCurrent, () => dom.maybe(plan.priceToShow, p => buildSmallPrice(centsToUSD(p)))),
          ]),
        ),
      ),
    ),
    // Part that will be collapsed
    cssCollapsable(
      cssPlanOptions(...plan.features.map(o => buildPlanOption(o))),
      // Divider with margin-top: auto - equivalent of empty div with flex-grow: 1
      cssDivider(),

      dom("div", { style: "align-self: flex-start; margin-bottom: 4px" },
        cssSave(
          dom.domComputed(plan.savings, num => `Save ${centsToUSD(num ?? 0)}`),
        ),
        dom("span", " / user / year", dom.style("opacity", "0.5")),
        dom.style("visibility", use => use(plan.savings) ? "visible" : "hidden"),
      ),

      dom.maybe(plan.priceToShow, price => buildMonthPrice(centsToUSD(price), " / user / month")),
      dom.maybe(!plan.price, () => buildMonthPrice("Free")),

      !(plan.comment || !options.single) ? null :
        dom("div",
          dom.style("display", "flex"),
          dom.style("justify-content", "space-between"),
          cssComment(plan.comment, dom.style("visibility", plan.comment ? "visible" : "hidden")),
        ),
      dom.maybe(isCurrent, () => [
        cssButtonWrapper(
          button = bigBasicButton(
            "Current plan",
            dom.style("text-align", "center"),
            testId("current-plan"),
          ),
        ),
      ]),
      dom.maybe(!isCurrent, () => [
        cssButtonWrapper(
          button = bigPrimaryButtonLink(
            unwrap(plan.buttonText()),
            dom.style("text-align", "center"),
            dom.attr("href", url),
            disableAfterClick(action),
            // We have link here, so make sure we don't click it by accident.
            dom.on("click", ev => (ev.stopPropagation(), ev.preventDefault())),
            testId("upgrade-plan"),
            loadingDots(
              dom.style("margin-left", "12px"),
              inlineStyle("--dot-size", "8px"),
              dom.show(action.busy),
            ),
          ),
        ),
      ]),
    ),
  );
}

/**
 * If the content is a string and looks like a Grist link (has a URL after a space), then renders an anchor tag.
 */
export function maybeLink(content: DomContents) {
  if (typeof content === "string") {
    // test if this is a link
    const parts = content.split(" ");
    const link = parts.pop();
    if (link?.startsWith("http")) {
      return cssLink({ href: link, target: "_blank" }, parts.join(" "));
    }
  }
  return content;
}

function disableAfterClick(onClick: () => unknown) {
  const disabled = Observable.create(null, false);
  async function clickHandler() {
    if (!onClick) { return; }
    disabled.set(true);
    try {
      await onClick();
    } finally {
      if (!disabled.isDisposed()) {
        disabled.set(false);
      }
    }
  }
  return [
    dom.autoDispose(disabled),
    dom.on("click", clickHandler),
    dom.prop("disabled", disabled),
  ];
}

function buildMonthPrice(price: DomContents, ...args: DomArg<HTMLElement>[]) {
  return dom("div",
    cssPrice(price),
    ...args,
  );
}

function buildSmallPrice(price: DomContents) {
  return cssPriceLine(
    cssPriceLine.cls("-small"),
    cssPrice(price,
      cssPrice.cls("-small"),
    ),
  );
}

export function buildPlanOption(text: DomContents) {
  return cssPlanOption(tickIcon("Tick"), maybeLink(text));
}

/**
 * Unwraps lifted DomContents, this should be part of grainjs.
 */
function unwrap(value: DomContents | (() => DomContents)): DomContents {
  return typeof value === "function" ? value() : value;
}

const cssDivider = styled("div", `
  margin-top: auto;
`);

const cssCurrentPlan = styled("div", `
  font-size: ${vars.mediumFontSize};
  font-weight: 500;
`);

const cssPlanOption = styled("div", `
  margin-bottom: 6px;
  --icon-ident: calc(16px + 8px);
  padding-left: var(--icon-ident);
`);

const tickIcon = styled(icon, `
  --icon-color: ${theme.accentIcon};
  margin-left: calc(-1 * var(--icon-ident));
  margin-right: 8px;
`);

const cssPriceLine = styled("div", `
  margin-top: 20px;
  &-small {
    margin-top: 0px;
  }
`);

const cssButtonWrapper = styled("div", `
  display: flex;
  flex-direction: column;
  margin-top: auto;
`);

const cssPrice = styled("span", `
  font-weight: 600;
  font-size: 24px;
  line-height: 32px;
  &-small {
    font-size: 16px;
  }
`);

const cssWhenCollapsed = styled("div", `
  display: none;
`);

const cssPlanName = styled(cssModalTitle, `
  margin: 0px;
  margin-bottom: 24px;
  font-size: 24px;
  font-weight: 600;
  display: flex;
  justify-content: space-between;
`);

const cssPlanOptions = styled("div", `
  flex-grow: 1;
`);

const cssComment = styled("div", `
  padding: 0px;
  margin: 6px 0px;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.2px;
  min-height: 1.5em;
`);

const cssAlwaysVisible = styled("div", `
  padding: 24px;
  padding-bottom: 0px;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
`);

const cssCollapsable = styled("div", `
  padding: 24px;
  padding-top: 0px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`);

export const cssPlan = styled("div", `
  border: 2px solid ${theme.accentBorder};
  border-radius: ${vars.controlBorderRadius};
  display: flex;
  flex-direction: column;
  width: 280px;
  border-color: ${theme.modalBorderDark};
  &:hover {
    border-color: ${theme.accentBorder};
  }
  &-single {
    border-color: ${theme.accentBorder};
    min-height: 400px;
  }
  &-single .${cssButtonWrapper.className} {
    display: none;
  }
  @media ${mediaSmall} {
    & {
      max-width: unset;
    }
    &-collapsed:not(&-current) {
      cursor: pointer;
    }
    &-collapsed .${cssCollapsable.className} {
      display: none;
    }
    &-collapsed .${cssWhenCollapsed.className} {
      display: block;
    }
    &-single {
      flex-grow: 1;
      min-height: unset;
    }
    &-current .${cssCollapsable.className} {
      display: none;
    }
  }
`);

// Badge with "Save $19" text
const cssSave = styled("div", `
  font-size: 13px;
  font-weight: 700;
  padding: 3px 6px;
  text-align: center;
  background-color: ${theme.accentBorder};
  color: white;
  display: inline-block;
  border-radius: 4px;
  align-self: center;
  flex: none;
  line-height: normal;
  margin-top: 24px;
`);
